/**
 * ChainLogo — official blockchain logo SVGs.
 * Renders the correct logo based on chain ID or name.
 */
import type { ReactElement } from 'react'

interface ChainLogoProps {
  chainId: number | string
  size?: number
  className?: string
}

// ─── XDC Network ─────────────────────────────────────────────────────────────
function XDCLogo({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className={className}>
      <circle cx="16" cy="16" r="16" fill="#1E3A5F" />
      <path d="M8 10.5L13.2 16L8 21.5H11.2L14.8 17.5L18.4 21.5H21.6L16.4 16L21.6 10.5H18.4L14.8 14.5L11.2 10.5H8Z" fill="white" />
    </svg>
  )
}

// ─── Base ────────────────────────────────────────────────────────────────────
function BaseLogo({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className={className}>
      <circle cx="16" cy="16" r="16" fill="#0052FF" />
      <path d="M16 26C21.5228 26 26 21.5228 26 16C26 10.4772 21.5228 6 16 6C10.7533 6 6.44 9.9471 6.04 15H19.5V17H6.04C6.44 22.0529 10.7533 26 16 26Z" fill="white" />
    </svg>
  )
}

// ─── Avalanche ───────────────────────────────────────────────────────────────
function AvalancheLogo({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className={className}>
      <circle cx="16" cy="16" r="16" fill="#E84142" />
      <path d="M20.7 21.5H23.5C23.9 21.5 24.1 21.4 24.2 21.1C24.4 20.8 24.3 20.5 24.1 20.2L16.9 7.9C16.7 7.6 16.4 7.4 16.1 7.4C15.7 7.4 15.4 7.6 15.2 7.9L12.7 12.3L14.6 15.6L18.8 21.1C19 21.3 19.2 21.5 19.5 21.5H20.7Z" fill="white" />
      <path d="M13.1 21.5H8.5C8.1 21.5 7.9 21.4 7.8 21.1C7.6 20.8 7.7 20.5 7.9 20.2L11 15.1L12.9 18.3L13.8 21.1C13.9 21.3 13.6 21.5 13.1 21.5Z" fill="white" />
    </svg>
  )
}

// ─── Ethereum ────────────────────────────────────────────────────────────────
function EthereumLogo({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className={className}>
      <circle cx="16" cy="16" r="16" fill="#627EEA" />
      <path d="M16 4V12.87L23 16.22L16 4Z" fill="white" fillOpacity="0.6" />
      <path d="M16 4L9 16.22L16 12.87V4Z" fill="white" />
      <path d="M16 21.97V28L23 17.62L16 21.97Z" fill="white" fillOpacity="0.6" />
      <path d="M16 28V21.97L9 17.62L16 28Z" fill="white" />
      <path d="M16 20.57L23 16.22L16 12.87V20.57Z" fill="white" fillOpacity="0.2" />
      <path d="M9 16.22L16 20.57V12.87L9 16.22Z" fill="white" fillOpacity="0.6" />
    </svg>
  )
}

// ─── Fallback ────────────────────────────────────────────────────────────────
function FallbackLogo({ size = 16, className, label }: { size?: number; className?: string; label?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className={className}>
      <circle cx="16" cy="16" r="16" fill="#333" />
      <text x="16" y="20" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold" fontFamily="system-ui">
        {(label || '?').charAt(0).toUpperCase()}
      </text>
    </svg>
  )
}

// ─── Chain ID → Logo map ─────────────────────────────────────────────────────

const LOGO_MAP: Record<number | string, (props: { size?: number; className?: string }) => ReactElement> = {
  // XDC
  50: XDCLogo,
  51: XDCLogo,
  // Base
  8453: BaseLogo,
  84532: BaseLogo,
  // Avalanche
  43114: AvalancheLogo,
  43113: AvalancheLogo,
  // Ethereum
  1: EthereumLogo,
  11155111: EthereumLogo, // Sepolia
}

export function ChainLogo({ chainId, size = 16, className }: ChainLogoProps) {
  const Logo = LOGO_MAP[chainId]
  if (Logo) return <Logo size={size} className={className} />
  return <FallbackLogo size={size} className={className} label={String(chainId)} />
}

export { XDCLogo, BaseLogo, AvalancheLogo, EthereumLogo }
